<template>
    <div>
      <InventoryHeaderTab />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Expiring Products</h5>
          <p class="page-sub">Monitor products approaching or past their expiry date</p>
        </div>
        <div class="page-actions">
  
          <!-- Period Filter -->
          <div class="period-wrap">
            <font-awesome-icon icon="clock" class="period-icon" />
            <select class="period-select" v-model="tableData.period" @change="tableData.page = 1; fetchData()">
              <option value="0">This Month</option>
              <option value="3">Next 3 Months</option>
              <option value="6">Next 6 Months</option>
              <option value="9">Next 9 Months</option>
              <option value="12">Next 12 Months</option>
              <option value="all">All Expired</option>
            </select>
          </div>
  
          <!-- Search -->
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search products..." v-model="query" />
          </div>
  
          <!-- Export -->
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#" @click="exportToPdf">
                <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
              </a></li>
              <li><a class="dropdown-item" href="#" @click="reusableMixinToCSV('expired-products.csv')">
                <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
              </a></li>
            </ul>
          </div>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted">Loading products...</p>
        </div>
  
        <!-- No Permission -->
        <div v-else-if="permission_status == 403" class="state-block">
          <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to view this resource.</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--green"><font-awesome-icon icon="check-circle" /></div>
          <p class="state-title">No expiring products</p>
          <p class="state-sub">There are no products expiring in the selected period.</p>
        </div>
  
        <!-- Table -->
        <template v-else>
  
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong> products
            </span>
            <div class="d-flex align-items-center gap-2">
              <label class="table-count">Rows:</label>
              <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option :value="pagination.total">All</option>
              </select>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th style="width:32%">Product</th>
                  <th>Quantity</th>
                  <th>Cost Price</th>
                  <th>Store</th>
                  <th>Best Before</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(product, index) in details" :key="index">
  
                  <td>
                    <div class="product-cell">
                      <div class="product-avatar"><font-awesome-icon icon="box" /></div>
                      <span class="product-name">{{ product.product_name }}</span>
                    </div>
                  </td>
  
                  <td>
                    <span class="qty-badge" :class="product.quantity < 5 ? 'qty-low' : ''">
                      {{ product.quantity }}
                    </span>
                  </td>
  
                  <td class="text-muted-cell">
                    {{ $filters.formatMoney(product.cost_price, currentUserCurrency?.symbol) }}
                  </td>
  
                  <td>
                    <span class="store-tag">{{ product.store_name }}</span>
                  </td>
  
                  <td>
                    <span class="expiry-badge" :class="expiryClass(product.best_before)">
                      <font-awesome-icon icon="calendar-times" class="me-1" />
                      {{ dateTime(product.best_before, 'short') }}
                    </span>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <div class="table-footer">
            <pagination :pagination="pagination" @prev="tableData.page = pagination.currentPage - 1; fetchData()" @next="tableData.page = pagination.currentPage + 1; fetchData()" />
          </div>
  
        </template>
      </div>
  
    </div>
  </template>
  
  <script>
  import InventoryHeaderTab from "./component/inventoryheadertab.vue";
  import jsPDF from "jspdf";
  import axios from "axios";
  import { useToast } from "vue-toastification";
  import { useStore } from "vuex";
  import { reusableMixin } from "../../../lib/reusableMixin";
  
  export default {
    components: { InventoryHeaderTab },
    mixins: [reusableMixin],
  
    data() {
      return {
        details: [], loading: false, query: null,
        selectedModel: [], allSelected: false,
        tableData: { page: 1,
          draw: 0, length: 5, search: '', column: 0,
          dir: 'desc', id: '', status: [0,1,2,3], query: '',
          period: this.$route.query.period ?? '0',
        },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast: useToast(), currentID: null, store: useStore(),
        permission_status: null,
      };
    },
  
    watch: { query() { this.fetchData(); } },
    created() { this.fetchData(); },
  
    methods: {
      expiryClass(date) {
        if (!date) return '';
        const diff = (new Date(date) - new Date()) / (1000 * 60 * 60 * 24);
        if (diff < 0)  return 'expiry-badge--expired';
        if (diff < 30) return 'expiry-badge--soon';
        return 'expiry-badge--ok';
      },
  
      exportToPdf() {
        const pdf = new jsPDF();
        pdf.setFont('Helvetica');
        pdf.text(105, 10, 'IMS', null, null, 'center');
        pdf.text(10, 20, 'Expired Product List');
        pdf.setFontSize(10);
        pdf.text(210, 20, `Generated at ${new Date().toISOString()}`);
        pdf.autoTable({ startY: 25, html: '.data-table', headStyles: { fillColor: [0, 74, 173] } });
        pdf.save(`Expired Products-${this.formatDateMMDDYY()}.pdf`);
      },
  
      selectAll() {
        this.selectedModel = this.allSelected ? this.details.map(u => u.id) : [];
      },
  
      fetchData(url = '/inventory/expired-products') {
        this.loading = true;
        this.tableData.query = this.query;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            this.loading = false;
            this.details = response.data.data.data;
            this.configPagination(response.data.data);
          })
          .catch(error => {
            this.loading = false;
            if (error?.response?.status == 403) {
              this.permission_status = 403;
              this.toast.error(error.response.data.message);
            }
          });
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  /* ── Period Filter ── */
  .period-wrap { position:relative; display:flex; align-items:center; }
  .period-icon { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .period-select {
    padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px;
    font-size:13px; color:#1a2b45; background:#fff; outline:none;
    cursor:pointer; transition:border-color .15s; appearance:auto;
  }
  .period-select:focus { border-color:#004AAD; }
  
  /* ── Search ── */
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:210px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .search-input::placeholder { color:#aab8cc; }
  
  /* ── Buttons ── */
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-outline::after { display:none !important; }
  
  /* ── Data Card ── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ── */
  .state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon  { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--green { background:rgba(22,163,74,.1); color:#16a34a; }
  .state-icon--red   { background:rgba(224,53,53,.1); color:#e03535; }
  .state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub   { font-size:13px; color:#7a90b0; margin:0; }
  
  /* ── Toolbar ── */
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  .rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  /* ── Table ── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:13px 16px; font-size:13.5px; vertical-align:middle; }
  .text-muted-cell { color:#4a5e78; }
  
  /* ── Product Cell ── */
  .product-cell   { display:flex; align-items:center; gap:10px; }
  .product-avatar { width:34px; height:34px; border-radius:8px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
  .product-name   { font-weight:600; color:#1a2b45; }
  
  /* ── Qty Badge ── */
  .qty-badge { display:inline-block; background:#edf4ff; color:#004AAD; border-radius:20px; padding:2px 10px; font-size:12.5px; font-weight:600; }
  .qty-low   { background:#fff0f0; color:#e03535; }
  
  /* ── Store Tag ── */
  .store-tag { display:inline-block; background:#f4f7fb; color:#4a5e78; border:1px solid #e4eaf2; border-radius:6px; padding:3px 10px; font-size:12.5px; }
  
  /* ── Expiry Badge ── */
  .expiry-badge { display:inline-flex; align-items:center; border-radius:6px; padding:3px 10px; font-size:12.5px; font-weight:600; border:1px solid transparent; }
  .expiry-badge--expired { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  .expiry-badge--soon    { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
  .expiry-badge--ok      { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  
  /* ── Footer ── */
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  /* ── Dropdown ── */
  .dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:170px; }
  .dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
  .dropdown-item:hover { background:#f4f7fb; }
  
  /* ── Responsive ── */
  @media(max-width:767px) {
    .page-header,.page-actions { flex-direction:column; align-items:stretch; }
    .search-input,.period-select { width:100%; }
    .period-wrap,.search-wrap { width:100%; }
  }
  </style>